import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from './ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  image_url: '',
  text: '',
  button_label: '',
  button_url: '',
  is_active: true,
};

/**
 * Admin tab for managing the site-wide promotional pop-up.
 * Backend enforces "one active at a time" — activating a new promo
 * auto-disables the previously active one.
 */
export default function PromotionsPanel({ token }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // promo being edited, null when idle, 'new' when creating
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/promotions`, { headers });
      setPromotions(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const openCreate = () => { setEditing('new'); setForm(BLANK); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      image_url: p.image_url || '',
      text: p.text || '',
      button_label: p.button_label || '',
      button_url: p.button_url || '',
      is_active: !!p.is_active,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.text && !form.image_url) {
      toast.error('Add some content — image or text');
      return;
    }
    if (form.button_label && !form.button_url) {
      toast.error('Button label needs a destination URL');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing === 'new') {
        await axios.post(`${API}/admin/promotions`, payload, { headers });
        toast.success('Promotion created');
      } else {
        await axios.put(`${API}/admin/promotions/${editing.id}`, payload, { headers });
        toast.success('Promotion updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion? It will stop showing immediately.')) return;
    try {
      await axios.delete(`${API}/admin/promotions/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (p) => {
    try {
      await axios.put(`${API}/admin/promotions/${p.id}`, { is_active: !p.is_active }, { headers });
      toast.success(p.is_active ? 'Deactivated' : 'Activated — now live site-wide');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const activeCount = promotions.filter((p) => p.is_active).length;

  return (
    <div className="space-y-6" data-testid="promotions-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-xs text-zinc-500 tracking-widest mb-1">PROMOTIONS</p>
          <h2 className="font-['Josefin_Sans'] text-3xl text-white">SITE POP-UPS</h2>
          <p className="text-zinc-500 text-sm mt-1">
            Shows a dismissible pop-up on every page. One active at a time — enabling a new one disables the previous.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="new-promotion-btn"
        >
          <Plus size={16} /> NEW POP-UP
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm font-mono flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> LOADING…
        </div>
      ) : promotions.length === 0 ? (
        <div className="border border-dashed border-zinc-800 p-10 text-center">
          <p className="text-zinc-500 text-sm mb-4">No promotions yet. Create one to start showing a pop-up site-wide.</p>
          <button onClick={openCreate} className="btn-primary inline-flex items-center gap-2" data-testid="empty-new-promo-btn">
            <Plus size={16} /> CREATE FIRST POP-UP
          </button>
        </div>
      ) : (
        <>
          {activeCount > 1 && (
            <div className="border border-amber-500/30 bg-amber-500/5 p-3 text-amber-300 text-sm font-mono">
              Warning: {activeCount} promotions marked active. Only the most recently updated will show.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="promotions-list">
            {promotions.map((p) => (
              <div
                key={p.id}
                className={`border p-4 bg-[#0A0A0A] ${
                  p.is_active ? 'border-[#B5FF00]/40 shadow-[0_0_30px_rgba(181,255,0,0.08)]' : 'border-zinc-800'
                }`}
                data-testid={`promotion-row-${p.id}`}
              >
                <div className="flex gap-4">
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="w-20 h-20 object-cover border border-zinc-800 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[10px] font-mono tracking-wider border ${
                        p.is_active
                          ? 'bg-[#B5FF00]/10 text-[#B5FF00] border-[#B5FF00]/30'
                          : 'bg-zinc-900 text-zinc-500 border-zinc-700'
                      }`}>
                        {p.is_active ? 'LIVE' : 'DISABLED'}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-500">v{p.version}</span>
                    </div>
                    <p className="text-white text-sm font-['Josefin_Sans'] line-clamp-2 mb-1">{p.text || '(no text)'}</p>
                    {p.button_label && (
                      <p className="font-mono text-[11px] text-zinc-400 truncate">
                        <span className="text-[#B5FF00]">{p.button_label}</span> → {p.button_url}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleToggle(p)}
                    className={`flex-1 text-xs font-mono tracking-widest py-2 border transition-colors ${
                      p.is_active
                        ? 'border-amber-500/40 text-amber-400 hover:bg-amber-500/5'
                        : 'border-[#B5FF00]/40 text-[#B5FF00] hover:bg-[#B5FF00]/5'
                    }`}
                    data-testid={`toggle-promo-${p.id}`}
                  >
                    {p.is_active ? 'DISABLE' : 'ENABLE'}
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    className="border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-600 px-3 py-2"
                    data-testid={`edit-promo-${p.id}`}
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="border border-zinc-800 text-zinc-400 hover:text-red-400 px-3 py-2"
                    data-testid={`delete-promo-${p.id}`}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Edit / create overlay — simple inline card to avoid another Dialog layer */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form
            onSubmit={handleSave}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-zinc-800 w-full max-w-xl max-h-[90vh] overflow-y-auto p-5 sm:p-6 space-y-4"
            data-testid="promotion-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-['Josefin_Sans'] text-xl text-white">
                {editing === 'new' ? 'NEW POP-UP' : 'EDIT POP-UP'}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">IMAGE</label>
              <ImageUploader
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                token={token}
                testIdPrefix="promotion-image"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">TEXT</label>
              <textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                rows={3}
                placeholder="Big launch this Friday — tickets going fast"
                className="w-full bg-[#050505] border border-zinc-800 text-white px-3 py-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                data-testid="promotion-text-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">BUTTON LABEL</label>
                <input
                  type="text"
                  value={form.button_label}
                  onChange={(e) => setForm({ ...form, button_label: e.target.value })}
                  placeholder="GET TICKETS"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="promotion-button-label-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">BUTTON URL</label>
                <input
                  type="text"
                  value={form.button_url}
                  onChange={(e) => setForm({ ...form, button_url: e.target.value })}
                  placeholder="/events/<id> or https://…"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="promotion-button-url-input"
                />
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="promotion-active-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="accent-[#B5FF00] w-5 h-5"
                data-testid="promotion-active-checkbox"
              />
              <div>
                <span className="font-mono text-xs text-white tracking-widest">ENABLE SITE-WIDE</span>
                <p className="text-zinc-500 text-xs mt-0.5">Any currently-live pop-up will be disabled automatically.</p>
              </div>
            </label>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="btn-secondary flex-1"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-promotion-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {editing === 'new' ? 'CREATE' : 'SAVE'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
